

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';

import ProductManagement from '../components/ProductManagement';
import UserManagement from '../components/UserManagement';
import CreateUser from '../components/CreateUser';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import CompletedOrders from '../components/CompletedOrders';
import CancelledOrders from '../components/CancelledOrders';
import ReturnReplaceRequests from '../components/ReturnReplaceRequests';
import CompletedCancelledRequests from '../components/CompletedCancelledRequests';
import AssignedPickups from './AssignedPickups';
import AppointmentManagement from '../components/AppointmentManagement';
import FranchiseStockManagement from '../components/FranchiseStockManagement';
import FranchiseManagement from '../components/FranchiseManagement';
import LowStockNotifications from '../components/LowStockNotifications';
import DeliveryTypeModal from '../components/DeliveryTypeModal';
import OffersManagement from '../components/OffersManagement';
import SubscriptionManagement from '../components/SubscriptionManagement';
import LoginActivityPanel from '../components/LoginActivityPanel';


const AdminDashboard = () => {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [unassignedOrders, setUnassignedOrders] = useState([]);
    const [deliveryPartners, setDeliveryPartners] = useState([]);
    const [assignedOrders, setAssignedOrders] = useState([]);
    const [activeTab, setActiveTab] = useState('unassignedOrders');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [orderToAssign, setOrderToAssign] = useState(null);
    const [selectedPartner, setSelectedPartner] = useState('');
    const [showDeliveryTypeModal, setShowDeliveryTypeModal] = useState(false);
    const [orderForDeliveryType, setOrderForDeliveryType] = useState(null);
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredUnassigned, setFilteredUnassigned] = useState([]);
    const [filteredAssigned, setFilteredAssigned] = useState([]);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [unassignedRes, partnersRes, assignedRes] = await Promise.all([
                apiClient.get('/orders/unassigned'),
                apiClient.get('/users/delivery-partners'),
                apiClient.get('/orders/assigned'),
            ]);

            setUnassignedOrders(unassignedRes.data);
            setDeliveryPartners(partnersRes.data);
            setAssignedOrders(assignedRes.data);
        } catch (err) {
            setError('Failed to fetch data.');
            console.error(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchAllData();
    }, [refreshFlag]);

    useEffect(() => {
        const lowercasedSearchTerm = searchTerm.toLowerCase();

        // Filter unassigned orders

        const unassignedResults = unassignedOrders.filter(order =>
            (order.customerInfo?.name || '').toLowerCase().includes(lowercasedSearchTerm) ||
            (order.customerInfo?.phone || '').toLowerCase().includes(lowercasedSearchTerm) ||
            (order.user?.email || '').toLowerCase().includes(lowercasedSearchTerm) ||
            (order.orderNumber || '').toLowerCase().includes(lowercasedSearchTerm)
        );
        setFilteredUnassigned(unassignedResults);

        // Filter assigned orders
        const assignedResults = assignedOrders.filter(order =>
            (order.customerInfo?.name || '').toLowerCase().includes(lowercasedSearchTerm) ||
            (order.customerInfo?.phone || '').toLowerCase().includes(lowercasedSearchTerm) ||
            (order.user?.email || '').toLowerCase().includes(lowercasedSearchTerm) ||
            (order.assignedTo?.name || '').toLowerCase().includes(lowercasedSearchTerm) ||
            (order.orderNumber || '').toLowerCase().includes(lowercasedSearchTerm)
        );
        setFilteredAssigned(assignedResults);
    }, [searchTerm, unassignedOrders, assignedOrders]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [activeTab]);

    const handleAssignClick = (order) => {
        setOrderToAssign(order);
        setShowAssignModal(true);
    };

    const handleAssignOrder = async () => {
        if (!selectedPartner) {
            alert('Please select a delivery partner.');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            await apiClient.post(
                '/delivery/assign',
                { orderId: orderToAssign._id, deliveryPersonId: selectedPartner },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert('Order assigned successfully!');
            setShowAssignModal(false);
            setOrderToAssign(null);
            setSelectedPartner('');
            setRefreshFlag(prev => !prev);
        } catch (err) {
            setError('Failed to assign order.');
            console.error(err);
        }
    };

    const handleDeliveryTypeClick = (order) => {
        setOrderForDeliveryType(order);
        setShowDeliveryTypeModal(true);
    };

    const handleSetDeliveryType = async (payload) => {
        try {
            const token = localStorage.getItem('token');
            await apiClient.post(
                '/delivery/update-delivery-type',
                { orderId: orderForDeliveryType._id, ...payload },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert('Delivery type updated successfully!');
            setShowDeliveryTypeModal(false);
            setOrderForDeliveryType(null);
            setRefreshFlag(prev => !prev);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to update delivery type.');
            console.error(err);
        }
    };

    const handleAdminStatusChange = async (orderId, status) => {
        const confirmChange = window.confirm(`Are you sure you want to change the status to '${status}'?`);
        if (!confirmChange) return;

        try {
            const token = localStorage.getItem('token');
            await apiClient.post(
                `/delivery/admin/update-status/${orderId}`,
                { status },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert('Status updated successfully!');
            setRefreshFlag(prev => !prev);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to update status.');
            console.error(err);
        }
    };

    if (loading) return <div className="text-center mt-10">Loading admin dashboard...</div>;
    if (error) return <div className="text-center text-red-500 mt-10">{error}</div>;

    return (
        <div className="dashboard-wrapper flex min-h-screen bg-gray-50">
            {/* Sidebar (Desktop) */}
            <aside className="w-64 bg-white shadow-lg hidden md:flex flex-col">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                    <h1 className="text-2xl font-extrabold text-green-700">Admin Panel</h1>
                    <LowStockNotifications align="left" />
                </div>
                <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                    {[
                        { id: 'unassignedOrders', label: 'Unassigned Deliveries' },
                        { id: 'assignedOrders', label: 'Assigned Deliveries' },
                        { id: 'products', label: 'Products' },
                        { id: 'users', label: 'Users' },
                        { id: 'createUser', label: 'Create User' },
                        { id: 'analytics', label: 'Analytics' },
                        { id: 'completedOrders', label: 'Completed' },
                        { id: 'cancelledOrders', label: 'Cancelled' },
                        { id: 'pendingReturns', label: 'Return Requests' },
                        { id: 'assignedPickups', label: 'Pickup Tasks' },
                        { id: 'completedReturns', label: 'Completed Returns' },
                        { id: 'cancelledReturns', label: 'Cancelled Returns' },
                        { id: 'appointments', label: 'Appointments' },
                        { id: 'manageFranchises', label: 'Manage Franchises' },
                        { id: 'franchise', label: 'Franchise Management' },
                        { id: 'offers', label: 'Offers' },
                        { id: 'subscriptions', label: 'Subscribe & Save' },
                        { id: 'loginActivity', label: 'Login Activity' },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                                activeTab === tab.id
                                    ? 'bg-green-50 text-green-700 shadow-sm'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                    <div className="mt-8 pt-4 border-t border-gray-100 space-y-2">
                        <Link to="/" className="block w-full text-left px-4 py-3 rounded-lg font-medium text-blue-600 hover:bg-blue-50 transition-all duration-200">
                            View Site
                        </Link>
                        <button onClick={async () => { await logout(); navigate('/login'); }} className="w-full text-left px-4 py-3 rounded-lg font-medium text-red-600 hover:bg-red-50 transition-all duration-200">
                            Logout
                        </button>
                    </div>
                </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1 p-4 md:p-8 overflow-y-auto">
                {/* Mobile Dropdown (Visible only on small screens) */}
                <div className="md:hidden mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h1 className="text-2xl font-bold">Admin Dashboard</h1>
                        <LowStockNotifications />
                    </div>
                    <select
                        value={activeTab} 
                        onChange={(e) => setActiveTab(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg shadow-sm bg-white focus:ring-green-500 focus:border-green-500"
                    >
                        <option value="unassignedOrders">Unassigned Deliveries</option>
                        <option value="assignedOrders">Assigned Deliveries</option>
                        <option value="products">Products</option>
                        <option value="users">Users</option>
                        <option value="createUser">Create User</option>
                        <option value="analytics">Analytics</option>
                        <option value="completedOrders">Completed</option>
                        <option value="cancelledOrders">Cancelled</option>
                        <option value="pendingReturns">Return Requests</option>
                        <option value="assignedPickups">Pickup Tasks</option>
                        <option value="completedReturns">Completed Returns</option>
                        <option value="cancelledReturns">Cancelled Returns</option>
                        <option value="appointments">Appointments</option>
                        <option value="manageFranchises">Manage Franchises</option>
                        <option value="franchise">Franchise Management</option>
                        <option value="offers">Offers</option>
                        <option value="subscriptions">Subscribe & Save</option>
                        <option value="loginActivity">Login Activity</option>
                    </select>
                </div>

            {activeTab === 'unassignedOrders' && (
                <div>
                    <div className="relative flex flex-col justify-center items-center mb-6 space-y-4 md:space-y-0">
                        <div className="flex items-center justify-center gap-3">
                            <div className="h-8 w-2 bg-green-500 rounded-full"></div>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-tight text-center">Unassigned Deliveries</h2>
                        </div>
                        <div className="md:absolute right-0 top-1/2 md:-translate-y-1/2 w-full md:w-auto mt-4 md:mt-0">
                            <input
                                type="text"
                                placeholder="Search orders..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full md:w-auto p-2 border rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>
                    </div>
                    <div className="space-y-4">
                        {filteredUnassigned.length > 0 ? filteredUnassigned.map(order => (
                            <div key={order._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                                {/* Card Header */}
                                <div className="bg-gray-50 border-b border-gray-100 px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div>
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Order ID</span>
                                        <p className="text-sm font-bold text-gray-900 mt-0.5">{order.orderNumber}</p>
                                    </div>
                                    <div className="flex flex-col sm:items-end">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Amount</span>
                                        <p className="text-sm font-bold text-green-600 mt-0.5">₹{order.totalPrice}</p>
                                    </div>
                                </div>
                                
                                {/* Card Body */}
                                <div className="p-6 flex flex-col md:flex-row gap-6">
                                    {/* Customer & Address Details */}
                                    <div className="flex-1 space-y-5">
                                        <div>
                                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                                                Customer Details
                                            </h4>
                                            <div className="bg-blue-50/50 rounded-lg p-3.5 space-y-2 border border-blue-100/50">
                                                <p className="text-sm text-gray-900 font-semibold">{order.customerInfo?.name}</p>
                                                <p className="text-sm text-gray-600 flex items-center gap-2">
                                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                                                    {order.customerInfo?.phone || 'N/A'}
                                                </p>
                                                <p className="text-sm text-gray-600 flex items-center gap-2">
                                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                                                    {order.user?.email || 'N/A'}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                                                Delivery Address
                                            </h4>
                                            <div className="bg-green-50/50 rounded-lg p-3.5 border border-green-100/50">
                                                <p className="text-sm text-gray-700 leading-relaxed font-medium">
                                                    {order.shippingAddress?.address},<br/>
                                                    {order.shippingAddress?.city} - {order.shippingAddress?.postalCode}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Products & Action */}
                                    <div className="flex-1 flex flex-col">
                                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path></svg>
                                            Order Items
                                        </h4>
                                        <div className="space-y-2.5 bg-gray-50/50 rounded-lg p-3 border border-gray-100/50 flex-1">
                                            {order.orderItems.map((item) => (
                                                <div key={item._id} className="flex items-center gap-3 bg-white p-2.5 rounded-md shadow-sm border border-gray-100">
                                                    <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0 bg-gray-100 border border-gray-100">
                                                        <img
                                                            src={item.product?.images?.[0]}
                                                            alt={item.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                                                        <p className="text-xs text-gray-500 mt-0.5">Pack: {item.size}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        
                                        <div className="mt-5 flex flex-wrap justify-end items-center gap-3">
                                            {order.deliveryType === 'instant' && (
                                                <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700 border border-orange-200">
                                                    ⚡ Instant (15 mins)
                                                </span>
                                            )}
                                            <button onClick={() => handleDeliveryTypeClick(order)} className="bg-white text-gray-700 border border-gray-300 px-4 py-2.5 rounded-lg hover:bg-gray-50 transition-all shadow-sm font-semibold text-sm">
                                                Set Delivery Type
                                            </button>
                                            <button onClick={() => handleAssignClick(order)} className="bg-green-600 text-white px-6 py-2.5 rounded-lg hover:bg-green-700 transition-all shadow-sm hover:shadow font-semibold text-sm flex items-center gap-2">
                                                Assign Delivery
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )) : <p>No unassigned orders found.</p>}
                    </div>
                </div>
            )}

            {activeTab === 'assignedOrders' && (
                <div>
                    <div className="relative flex flex-col justify-center items-center mb-6 space-y-4 md:space-y-0">
                        <div className="flex items-center justify-center gap-3">
                            <div className="h-8 w-2 bg-green-500 rounded-full"></div>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-tight text-center">Assigned Deliveries</h2>
                        </div>
                        <div className="md:absolute right-0 top-1/2 md:-translate-y-1/2 w-full md:w-auto mt-4 md:mt-0">
                            <input
                                type="text"
                                placeholder="Search orders..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full md:w-auto p-2 border rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>
                    </div>
                    <div className="space-y-4">
                        {filteredAssigned.length > 0 ? filteredAssigned.map(order => (
                            <div key={order._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                                {/* Card Header */}
                                <div className="bg-gray-50 border-b border-gray-100 px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <div>
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Order ID</span>
                                            <p className="text-sm font-bold text-gray-900 mt-0.5">{order.orderNumber}</p>
                                        </div>
                                        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 capitalize mt-2 sm:mt-0 border border-blue-200">
                                            {order.status}
                                        </span>
                                        {order.deliveryType === 'instant' && (
                                            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700 mt-2 sm:mt-0 border border-orange-200">
                                                ⚡ Instant (15 mins)
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-col sm:items-end">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Amount</span>
                                        <p className="text-sm font-bold text-green-600 mt-0.5">₹{order.totalPrice}</p>
                                    </div>
                                </div>
                                
                                {/* Card Body */}
                                <div className="p-6 flex flex-col md:flex-row gap-6">
                                    {/* Customer & Assignment Details */}
                                    <div className="flex-1 space-y-5">
                                        <div>
                                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                                                Customer Details
                                            </h4>
                                            <div className="bg-blue-50/50 rounded-lg p-3.5 space-y-2 border border-blue-100/50">
                                                <p className="text-sm text-gray-900 font-semibold">{order.customerInfo?.name}</p>
                                                <p className="text-sm text-gray-600 flex items-center gap-2">
                                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                                                    {order.customerInfo?.phone || 'N/A'}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                                                Delivery Address
                                            </h4>
                                            <div className="bg-green-50/50 rounded-lg p-3.5 border border-green-100/50">
                                                <p className="text-sm text-gray-700 leading-relaxed font-medium">
                                                    {order.shippingAddress?.address},<br/>
                                                    {order.shippingAddress?.city} - {order.shippingAddress?.postalCode}
                                                </p>
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                                Assigned Delivery Partner
                                            </h4>
                                            <div className="bg-purple-50/50 rounded-lg p-3.5 border border-purple-100/50 flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">{order.assignedTo?.name}</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">{order.assignedTo?.email}</p>
                                                </div>
                                                {order.isAutoAssigned && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded text-[10px] font-bold bg-blue-100 text-blue-800 border border-blue-200">
                                                        Auto-Assigned
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Products & Action */}
                                    <div className="flex-1 flex flex-col">
                                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path></svg>
                                            Order Items
                                        </h4>
                                        <div className="space-y-2.5 bg-gray-50/50 rounded-lg p-3 border border-gray-100/50 flex-1">
                                            {order.orderItems.map((item) => (
                                                <div key={item._id} className="flex items-center gap-3 bg-white p-2.5 rounded-md shadow-sm border border-gray-100">
                                                    <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0 bg-gray-100 border border-gray-100">
                                                        <img
                                                            src={item.product?.images?.[0]}
                                                            alt={item.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                                                        <p className="text-xs text-gray-500 mt-0.5">Pack: {item.size}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex flex-wrap gap-2 w-full mt-4 justify-end">
                                            <button onClick={() => handleDeliveryTypeClick(order)} className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors shadow-sm">Set Delivery Type</button>
                                            <button onClick={() => handleAdminStatusChange(order._id, 'delivered')} className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors shadow-sm">Complete</button>
                                            <button onClick={() => handleAdminStatusChange(order._id, 'cancelled')} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors shadow-sm">Cancel</button>
                                            <button onClick={() => handleAssignClick(order)} className="bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600 transition-colors shadow-sm">Reassign</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )) : <p>No assigned orders found.</p>}
                    </div>
                </div>
            )}

            {activeTab === 'pendingReturns' && (
                <ReturnReplaceRequests
                    deliveryPartners={deliveryPartners}
                    setActiveTab={setActiveTab}
                    setRefreshFlag={setRefreshFlag}
                    showFranchiseInfo
                />
            )}
            {activeTab === 'assignedPickups' && <AssignedPickups setActiveTab={setActiveTab} showFranchiseInfo />}
            {activeTab === 'completedReturns' && <CompletedCancelledRequests type="completed" showFranchiseInfo />}
            {activeTab === 'cancelledReturns' && <CompletedCancelledRequests type="cancelled" showFranchiseInfo />}
            {activeTab === 'products' && <ProductManagement />}
            {activeTab === 'users' && <UserManagement />}
            {/* {activeTab === 'users' && <UserManagement onUserListUpdated={fetchAllData} />} */}
            {activeTab === 'createUser' && <CreateUser onUserCreated={fetchAllData} />}
            {activeTab === 'analytics' && <AnalyticsDashboard />}
            {activeTab === 'completedOrders' && <CompletedOrders refreshFlag={refreshFlag} showFranchiseInfo />}
            {activeTab === 'cancelledOrders' && <CancelledOrders refreshFlag={refreshFlag} showFranchiseInfo />}
            { activeTab === 'appointments' && <AppointmentManagement /> }
            { activeTab === 'manageFranchises' && <FranchiseManagement /> }
            { activeTab === 'franchise' && <FranchiseStockManagement /> }
            { activeTab === 'offers' && <OffersManagement /> }
            { activeTab === 'subscriptions' && <SubscriptionManagement /> }
            { activeTab === 'loginActivity' && <LoginActivityPanel /> }


            {showAssignModal && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 overflow-y-auto h-full w-full flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        {/* Modal Header */}
                        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-800">Assign Delivery Partner</h3>
                            <button onClick={() => setShowAssignModal(false)} className="text-gray-400 hover:text-red-500 transition-colors outline-none rounded-full p-1 hover:bg-red-50">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                        </div>
                        
                        {/* Modal Body */}
                        <div className="p-6 space-y-6">
                            <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100/50 flex items-start gap-4">
                                <div className="bg-blue-100 p-2.5 rounded-lg text-blue-600 mt-0.5">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path></svg>
                                </div>
                                <div>
                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Selected Order</p>
                                    <p className="text-sm font-bold text-gray-900">{orderToAssign?.orderNumber}</p>
                                    <p className="text-sm text-gray-600 mt-0.5">Customer: {orderToAssign?.customerInfo?.name}</p>
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-gray-700 pl-1">Select Partner</label>
                                <div className="relative">
                                    <select 
                                        onChange={(e) => setSelectedPartner(e.target.value)} 
                                        className="w-full pl-4 pr-10 py-3 bg-gray-50/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none outline-none transition-all font-medium text-sm text-gray-700 cursor-pointer shadow-sm hover:bg-gray-50"
                                    >
                                        <option value="">-- Choose a delivery partner --</option>
                                        {deliveryPartners.map(partner => (
                                            <option key={partner._id} value={partner._id}>{partner.name} ({partner.email})</option>
                                        ))}
                                    </select>
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                            <button onClick={() => setShowAssignModal(false)} className="px-5 py-2.5 rounded-xl font-medium text-sm text-white bg-red-500 hover:bg-red-600 shadow-sm transition-colors">
                                Cancel
                            </button>
                            <button 
                                onClick={handleAssignOrder} 
                                className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${selectedPartner ? 'bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg' : 'bg-green-600 text-white cursor-not-allowed opacity-80'}`}
                                disabled={!selectedPartner}
                            >
                                Confirm Assignment
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeliveryTypeModal && orderForDeliveryType && (
                <DeliveryTypeModal
                    order={orderForDeliveryType}
                    onClose={() => { setShowDeliveryTypeModal(false); setOrderForDeliveryType(null); }}
                    onSubmit={handleSetDeliveryType}
                />
            )}
            </main>
        </div>
    );
};

export default AdminDashboard;